import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Star, MessageCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { BUSINESS, ENQUIRY_TYPES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", enquiry_type: "Gold Jewellery", message: "" };

const fieldCls =
  "w-full bg-transparent border border-gold/30 rounded-sm px-4 py-3.5 text-base text-charcoal placeholder:text-charcoal/35 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors duration-300";

export const EnquiryForm = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedInfo, setSubmittedInfo] = useState({ name: "", enquiry_type: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you! Your enquiry has been received. We will reach out to you shortly.");
      setSubmittedInfo({ name: form.name, enquiry_type: form.enquiry_type });
      setForm(initial);
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="enquiry" data-testid="enquiry-section" className="py-20 md:py-32 bg-royal relative grain overflow-hidden scroll-mt-20">
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              chapter="10"
              overline="Enquire"
              title="Tell Us What You're Looking For"
              description="Share a few details and our team will get in touch with designs, availability and answers — no obligation, no pressure."
              dark
            />
            <Reveal className="-mt-6 md:-mt-10 space-y-4">
              {["Personal response from our showroom team", "Designs and availability shared on WhatsApp", "Transparent pricing, always"].map((t) => (
                <div key={t} className="flex items-center gap-3 text-sm font-light text-ivory/70">
                  <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" aria-hidden="true" />
                  {t}
                </div>
              ))}
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form
              data-testid="enquiry-form"
              onSubmit={submit}
              className="bg-ivory rounded-sm p-7 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.3)]"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="enquiry-name" className="block text-[11px] tracking-[0.2em] uppercase text-charcoal/50 font-medium mb-2">Name *</label>
                  <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your full name" className={fieldCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className="block text-[11px] tracking-[0.2em] uppercase text-charcoal/50 font-medium mb-2">Phone Number *</label>
                  <input id="enquiry-phone" data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="+91" className={fieldCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="block text-[11px] tracking-[0.2em] uppercase text-charcoal/50 font-medium mb-2">Email</label>
                  <input id="enquiry-email" data-testid="enquiry-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-type" className="block text-[11px] tracking-[0.2em] uppercase text-charcoal/50 font-medium mb-2">Enquiry Type</label>
                  <select id="enquiry-type" data-testid="enquiry-type-select" value={form.enquiry_type} onChange={set("enquiry_type")} className={fieldCls}>
                    {ENQUIRY_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className="block text-[11px] tracking-[0.2em] uppercase text-charcoal/50 font-medium mb-2">Message</label>
                  <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about the piece or occasion…" className={`${fieldCls} resize-none`} />
                </div>
              </div>
              <button
                data-testid="enquiry-submit-button"
                type="submit"
                disabled={sending}
                className="mt-7 w-full sm:w-auto px-10 py-4 bg-royal text-ivory text-sm tracking-[0.15em] uppercase font-medium rounded-sm hover:bg-royal-light transition-colors duration-300 flex items-center justify-center gap-2.5 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Enquiry"} <Send size={14} strokeWidth={1.5} />
              </button>
              <p className="mt-4 text-xs text-charcoal/45 font-light">
                Your details stay with us and are used only to respond to your enquiry.
              </p>
              <AnimatePresence>
                {submitted && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-5 pt-5 border-t border-gold/25"
                  >
                    <a
                      data-testid="enquiry-whatsapp-followup-button"
                      href={`https://wa.me/918584886600?text=${encodeURIComponent(
                        `Hello Jeweller Kalika Prasad, this is ${submittedInfo.name}. I have just submitted an enquiry about ${submittedInfo.enquiry_type} on your website and would be happy to continue the conversation here. Thank you!`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2.5 px-6 py-3 border border-gold text-gold-muted text-[13px] tracking-[0.1em] uppercase font-medium rounded-sm hover:bg-gold hover:text-royal-deep transition-colors duration-300"
                    >
                      <MessageCircle size={15} strokeWidth={1.5} /> Chat on WhatsApp
                    </a>
                    <p className="mt-2.5 text-[11px] text-charcoal/40 font-light">
                      Optional — continue your enquiry instantly on WhatsApp.
                    </p>
                    <p
                      data-testid="enquiry-review-prompt"
                      className="mt-4 text-sm font-light text-charcoal/60 flex items-center gap-2"
                    >
                      <Star size={13} strokeWidth={1.5} className="text-gold-muted shrink-0" />
                      <span>
                        Enjoyed your experience with us?{" "}
                        <a
                          data-testid="enquiry-share-experience-link"
                          href={BUSINESS.reviewsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="gold-underline text-gold-muted font-medium hover:text-royal transition-colors duration-300"
                        >
                          Share your experience on Google
                        </a>
                      </span>
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
